import { t } from '../i18n.js';

  class AvatarPicker extends HTMLElement {

    // ── A. Defaults ────────────────────────────────────────────────────
    static get DEFAULT_SIZE()       { return 80; }   // px
    static get OUTPUT_SIZE()        { return 80; }  // max output px (longest edge)
    static get JPEG_QUALITY()       { return 0.88; }

    // ── B. Observed attributes ─────────────────────────────────────────
    static get observedAttributes() { return ["size", "initials", "src"]; }

    constructor() {
      super();
      this._shadow      = this.attachShadow({ mode: "open" });
      this._currentSrc  = null;   // base64 data-URL stored in DB
      this._initials    = "";
      this._size        = AvatarPicker.DEFAULT_SIZE;

      // crop state
      this._cropSrc    = null;
      this._cropScale  = 1;
      this._cropX      = 0;
      this._cropY      = 0;
      this._dragging   = false;
      this._dragStart  = null;
      this._pinchDist  = null;
      this._naturalW   = 0;
      this._naturalH   = 0;

      this._globalsBound = false;
    }

    connectedCallback() {
      this._size     = parseInt(this.getAttribute("size")     || AvatarPicker.DEFAULT_SIZE);
      this._initials = (this.getAttribute("initials") || "").toUpperCase().slice(0, 2);
      const srcAttr  = this.getAttribute("src");
      if (srcAttr) this._currentSrc = srcAttr;
      this._render();
      this._bindEvents();
    }

    attributeChangedCallback(name, _, val) {
      if (!this._shadow.innerHTML) return;
      if (name === "size")     { this._size = parseInt(val) || AvatarPicker.DEFAULT_SIZE; this._render(); this._bindEvents(); }
      if (name === "initials") { this._initials = (val || "").toUpperCase().slice(0, 2); this._renderAvatar(); }
      if (name === "src")      { this.src = val; }
    }

    // ── C. Public setter: load an existing base64 or URL ─────────────
    set src(val) {
      this._currentSrc = val || null;
      this._renderAvatar();
    }

    get src() { return this._currentSrc; }

    // ── D. Open the file picker ───────────────────────────────────────
    _openFilePicker() {
      this._shadow.getElementById("file-input").click();
    }

    // ── E. Handle file selected ───────────────────────────────────────
    _onFileSelected(e) {
      const file = e.target.files[0];
      e.target.value = "";
      if (!file || !file.type.startsWith("image/")) return;

      const reader = new FileReader();
      reader.onload = ev => this._openCrop(ev.target.result);
      reader.readAsDataURL(file);
    }

    // ── F. Open crop modal ────────────────────────────────────────────
    _openCrop(src) {
      this._cropSrc   = src;
      this._cropScale = 1;
      this._cropX     = 0;
      this._cropY     = 0;

      const overlay = this._shadow.getElementById("crop-overlay");
      const img     = this._shadow.getElementById("crop-img");
      const zoom    = this._shadow.getElementById("crop-zoom");
      const hint    = this._shadow.getElementById("zoom-val");

      img.src     = src;
      zoom.value  = "1";
      if (hint) hint.textContent = "1.0×";

      img.onload = () => {
        console.log("naturalWidth: %d, naturalHeight: %d", img.naturalWidth, img.naturalHeight);
        this._naturalW = img.naturalWidth;
        this._naturalH = img.naturalHeight;
        this._updateCropTransform();
      };

      overlay.classList.add("visible");
    }

    _closeCrop() {
      this._shadow.getElementById("crop-overlay").classList.remove("visible");
    }

    // ── G. Recompute image transform inside stage ─────────────────────
    _updateCropTransform() {
      const img   = this._shadow.getElementById("crop-img");
      const stage = this._shadow.getElementById("crop-stage");
      if (!img || !stage) return;

      console.log("img: %o", img);
      console.log("stage: %o", stage);
      

      const sw = stage.offsetWidth  || 280;
      const sh = stage.offsetHeight || 280;
      const nw = this._naturalW || img.naturalWidth  || sw;
      const nh = this._naturalH || img.naturalHeight || sh;

      const base = Math.min(sw / nw, sh / nh);
      const s    = base * this._cropScale;

      img.style.width  = (nw * s) + "px";
      img.style.height = (nh * s) + "px";
      img.style.left   = (sw / 2 - nw * s / 2 + this._cropX) + "px";
      img.style.top    = (sh / 2 - nh * s / 2 + this._cropY) + "px";
    }

    // ── H. Apply crop + resample ──────────────────────────────────────
    _applyCrop() {
      const OUTPUT = AvatarPicker.OUTPUT_SIZE;
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = OUTPUT;

      const ctx = canvas.getContext("2d");

      // Circular clip
      ctx.beginPath();
      ctx.arc(OUTPUT / 2, OUTPUT / 2, OUTPUT / 2, 0, Math.PI * 2);
      ctx.clip();

      const img      = this._shadow.getElementById("crop-img");
      const stage    = this._shadow.getElementById("crop-stage");
      const sw = stage.offsetWidth, sh = stage.offsetHeight;
      const nw = this._naturalW, nh = this._naturalH;
      const base     = Math.min(sw / nw, sh / nh);
      const s        = base * this._cropScale;

      // Circle radius in stage coords (half the stage, leaving 10% padding)
      const circleR  = Math.min(sw, sh) * 0.40;
      const cLeft    = sw / 2 - circleR;
      const cTop     = sh / 2 - circleR;

      // Image offset (top-left of rendered img)
      const ix = sw / 2 - nw * s / 2 + this._cropX;
      const iy = sh / 2 - nh * s / 2 + this._cropY;

      // Source rect in natural image pixels
      const srcX = (cLeft - ix) / s;
      const srcY = (cTop  - iy) / s;
      const srcW = circleR * 2 / s;

      console.log("src: %d, %d, %d, %d", srcX, srcY, srcW, srcW);
      console.log("dst: %d, %d, %d, %d", 0, 0, OUTPUT, OUTPUT);
      ctx.drawImage(img, srcX, srcY, srcW, srcW, 0, 0, OUTPUT, OUTPUT);

      const mimeType = "image/jpeg";
      const dataURL  = canvas.toDataURL(mimeType, AvatarPicker.JPEG_QUALITY);

      this._currentSrc = dataURL;
      this._renderAvatar();
      this._closeCrop();

      // Fire event
      this.dispatchEvent(new CustomEvent("avatar-change", {
        bubbles: true,
        composed: true,
        detail: {
          base64:   dataURL,
          mimeType,
          width:    OUTPUT,
          height:   OUTPUT,
        }
      }));
    }

    // ── I. Re-render just the avatar circle ───────────────────────────
    _renderAvatar() {
      const avatarEl = this._shadow.getElementById("avatar-circle");
      if (!avatarEl) return;

      const s = this._size;
      const fontSize = Math.round(s * 0.32);

      if (this._currentSrc) {
        avatarEl.innerHTML = `
          <img id="av-img" src="${this._currentSrc}" alt="Avatar"
               style="width:${s}px;height:${s}px;border-radius:50%;object-fit:cover;display:block;"
               onerror="this.style.display='none';document.getElementById('av-fallback').style.display='flex'" />
          <div id="av-fallback" style="display:none;width:${s}px;height:${s}px;border-radius:50%;
               background:#ddeef9;color:#2a6496;align-items:center;justify-content:center;
               font-size:${fontSize}px;font-weight:500;">
            ${this._initials || "?"}
          </div>`;
      } else {
        avatarEl.innerHTML = `
          <div style="width:${s}px;height:${s}px;border-radius:50%;
               background:#ddeef9;color:#2a6496;display:flex;align-items:center;justify-content:center;
               font-size:${fontSize}px;font-weight:500;">
            ${this._initials || `
              <svg width="${Math.round(s*0.38)}" height="${Math.round(s*0.38)}" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4.5" stroke="currentColor" stroke-width="1.5"/>
                <path d="M3 21c0-4.418 4.03-8 9-8s9 3.582 9 8"
                      stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>`}
          </div>`;
      }
    }

    // ── J. Bind internal events ───────────────────────────────────────
    _bindEvents() {
      const root = this._shadow;

      root.getElementById("edit-btn")
          ?.addEventListener("click", () => this._openFilePicker());

      root.getElementById("file-input")
          ?.addEventListener("change", e => this._onFileSelected(e));

      // Zoom slider
      root.getElementById("crop-zoom")?.addEventListener("input", e => {
        this._cropScale = parseFloat(e.target.value);
        const hint = root.getElementById("zoom-val");
        if (hint) hint.textContent = this._cropScale.toFixed(1) + "×";
        this._updateCropTransform();
      });

      // Crop action buttons
      root.getElementById("crop-apply") ?.addEventListener("click", () => this._applyCrop());
      root.getElementById("crop-cancel")?.addEventListener("click", () => this._closeCrop());
      root.getElementById("crop-close") ?.addEventListener("click", () => this._closeCrop());

      // Drag in crop stage (mouse)
      const stage = root.getElementById("crop-stage");
      if (stage) {
        stage.addEventListener("mousedown", e => {
          this._dragging  = true;
          this._dragStart = { x: e.clientX - this._cropX, y: e.clientY - this._cropY };
          e.preventDefault();
        });

        // Touch drag
        stage.addEventListener("touchstart", e => {
          if (e.touches.length === 1) {
            const t = e.touches[0];
            this._dragging  = true;
            this._pinchDist = null;
            this._dragStart = { x: t.clientX - this._cropX, y: t.clientY - this._cropY };
          } else if (e.touches.length === 2) {
            this._dragging  = false;
            this._pinchDist = this._getTouchDist(e.touches);
          }
        }, { passive: true });

        stage.addEventListener("touchmove", e => {
          if (e.touches.length === 2) {
            // Pinch-to-zoom
            const newDist = this._getTouchDist(e.touches);
            if (this._pinchDist) {
              const ratio = newDist / this._pinchDist;
              this._cropScale = Math.min(5, Math.max(0.5, this._cropScale * ratio));
              const zoom = root.getElementById("crop-zoom");
              if (zoom) zoom.value = this._cropScale;
              const hint = root.getElementById("zoom-val");
              if (hint) hint.textContent = this._cropScale.toFixed(1) + "×";
              this._updateCropTransform();
            }
            this._pinchDist = newDist;
          } else if (e.touches.length === 1 && this._dragging && this._dragStart) {
            const t = e.touches[0];
            this._cropX = t.clientX - this._dragStart.x;
            this._cropY = t.clientY - this._dragStart.y;
            this._updateCropTransform();
          }
        }, { passive: true });

        stage.addEventListener("touchend", () => {
          this._dragging = false;
          this._pinchDist = null;
        });
      }

      // Global mouse events (move + up)
      if (!this._globalsBound) {
        this._globalsBound = true;
        window.addEventListener("mousemove", e => {
          if (!this._dragging || !this._dragStart) return;
          this._cropX = e.clientX - this._dragStart.x;
          this._cropY = e.clientY - this._dragStart.y;
          this._updateCropTransform();
        });
        window.addEventListener("mouseup", () => { this._dragging = false; });
      }
    }

    _getTouchDist(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    }

    // ── K. Full render ────────────────────────────────────────────────
    _render() {
      const s         = this._size;
      const btnSize   = Math.max(22, Math.round(s * 0.3));
      const iconSize  = Math.round(btnSize * 0.52);
      const stageSize = 280;

      this._shadow.innerHTML = `
        <style>
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

          :host {
            display: inline-block;
            position: relative;
            font-family: system-ui, sans-serif;
          }

          /* ── Avatar wrapper ── */
          .av-wrap {
            position: relative;
            display: inline-block;
            width: ${s}px;
            height: ${s}px;
          }

          #avatar-circle {
            width: ${s}px;
            height: ${s}px;
            border-radius: 50%;
            overflow: hidden;
            border: 0px solid #fff;
            box-shadow: 0 0 0 0.5px #e0dfd7;
          }

          .edit-btn {
            position: absolute;
            bottom: 0;
            right: 0;
            width: ${btnSize}px;
            height: ${btnSize}px;
            border-radius: 50%;
            background: #1a1a1a;
            color: #fff;
            border: 2px solid #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: opacity 0.15s, transform 0.15s;
            -webkit-tap-highlight-color: transparent;
            box-shadow: 0 1px 4px rgba(0,0,0,0.18);
          }

          .edit-btn:hover  { opacity: 0.82; }
          .edit-btn:active { transform: scale(0.9); }

          /* ── Crop overlay ── */
          .crop-overlay {
            display: none;
            position: fixed;
            inset: 0;
            z-index: 9999;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
            align-items: center;
            justify-content: center;
            padding: 1rem;
          }

          .crop-overlay.visible { display: flex; }

          /* ── Crop modal ── */
          .crop-modal {
            background: #fff;
            border: 0.5px solid #e0dfd7;
            border-radius: 16px;
            overflow: hidden;
            width: 100%;
            max-width: 340px;
            box-shadow: 0 8px 40px rgba(0,0,0,0.18);
            animation: modal-in 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          @keyframes modal-in {
            from { transform: scale(0.92) translateY(8px); opacity: 0; }
            to   { transform: scale(1)    translateY(0);   opacity: 1; }
          }

          .crop-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 14px 16px 12px;
            border-bottom: 0.5px solid #e0dfd7;
          }

          .crop-title {
            font-size: 14px;
            font-weight: 500;
            color: #1a1a1a;
          }

          .crop-close-btn {
            width: 28px; height: 28px;
            border-radius: 50%;
            border: 0.5px solid #e0dfd7;
            background: #f5f5f3;
            color: #888;
            display: flex; align-items: center; justify-content: center;
            cursor: pointer;
            transition: background 0.12s, color 0.12s;
            -webkit-tap-highlight-color: transparent;
          }
          .crop-close-btn:hover { background: #eee; color: #1a1a1a; }

          /* ── Stage ── */
          .crop-stage {
            position: relative;
            width: 100%;
            height: ${stageSize}px;
            overflow: hidden;
            background: #111;
            cursor: grab;
            user-select: none;
            -webkit-user-select: none;
            touch-action: none;
          }

          .crop-stage:active { cursor: grabbing; }

          #crop-img {
            position: absolute;
            pointer-events: none;
            transform-origin: top left;
            will-change: width, height, left, top;
          }

          /* Circular guide overlay — CSS only, no canvas needed */
          .crop-guide {
            position: absolute;
            inset: 0;
            pointer-events: none;
          }

          .crop-guide::before {
            content: "";
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
          }

          .crop-guide::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: ${Math.round(stageSize * 0.795)}px;
            height: ${Math.round(stageSize * 0.795)}px;
            transform: translate(-50%, -50%);
            border-radius: 50%;
            border: 2px solid rgba(255,255,255,0.9);
            box-shadow:
              0 0 0 9999px rgba(0,0,0,0.42),
              0 0 0 1px rgba(255,255,255,0.15);
          }

          /* ── Zoom controls ── */
          .crop-controls {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 16px;
            border-top: 0.5px solid #e0dfd7;
            border-bottom: 0.5px solid #e0dfd7;
          }

          .zoom-icon {
            color: #aaa;
            display: flex;
            align-items: center;
            flex-shrink: 0;
          }

          input[type="range"] {
            flex: 1;
            height: 4px;
            border-radius: 2px;
            appearance: none;
            -webkit-appearance: none;
            background: #e0dfd7;
            outline: none;
            cursor: pointer;
          }

          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 18px; height: 18px;
            border-radius: 50%;
            background: #1a1a1a;
            border: 2px solid #fff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
            cursor: grab;
          }

          input[type="range"]::-moz-range-thumb {
            width: 18px; height: 18px;
            border-radius: 50%;
            background: #1a1a1a;
            border: 2px solid #fff;
            cursor: grab;
          }

          .zoom-val {
            font-size: 11px;
            color: #aaa;
            width: 30px;
            text-align: right;
            flex-shrink: 0;
          }

          /* ── Action buttons ── */
          .crop-actions {
            display: flex;
            gap: 8px;
            padding: 12px 16px;
          }

          .btn {
            flex: 1;
            padding: 10px 12px;
            font: inherit;
            font-size: 14px;
            font-weight: 500;
            border-radius: 9px;
            cursor: pointer;
            transition: opacity 0.15s, transform 0.1s;
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
          }

          .btn:active { transform: scale(0.97); }

          .btn-cancel {
            background: #f5f5f3;
            color: #555;
            border: 0.5px solid #d0cfc7;
          }

          .btn-cancel:hover { background: #ededea; }

          .btn-apply {
            background: #4E3930;
            color: #fff;
            border: none;
          }

          .btn-apply:hover { opacity: 0.85; }

          /* ── Hint ── */
          .crop-hint {
            font-size: 11px;
            color: #bbb;
            text-align: center;
            padding: 0 16px 10px;
          }

          /*
          @media (prefers-color-scheme: dark) {
            .crop-modal     { background: #1e1e1c; border-color: #333330; }
            .crop-header    { border-bottom-color: #333330; }
            .crop-title     { color: #f0ede8; }
            .crop-close-btn { background: #2a2a28; border-color: #333330; color: #888; }
            .crop-close-btn:hover { background: #333330; color: #f0ede8; }
            .crop-controls  { border-color: #333330; }
            .crop-actions   { border-top-color: #333330; }
            .btn-cancel     { background: #2a2a28; border-color: #333330; color: #888; }
            .btn-cancel:hover { background: #333330; }
            .btn-apply      { background: #f0ede8; color: #1a1a1a; }
            input[type="range"] { background: #333330; }
            input[type="range"]::-webkit-slider-thumb { background: #f0ede8; }
            input[type="range"]::-moz-range-thumb     { background: #f0ede8; }
          }
          */
        </style>

        <!-- ── Avatar ── -->
        <div class="av-wrap">
          <div id="avatar-circle"></div>
          <button class="edit-btn" id="edit-btn" aria-label="${t("change-avatar")}">
            <svg width="${iconSize}" height="${iconSize}" viewBox="0 0 14 14" fill="none">
              <path d="M9.5 2l2.5 2.5-7 7H2.5v-2.5l7-7z"
                    stroke="currentColor" stroke-width="1.3"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <input type="file" id="file-input" accept="image/*" style="display:none" />
        </div>

        <!-- ── Crop modal (fixed overlay, outside the stacking context) ── -->
        <div class="crop-overlay" id="crop-overlay" role="dialog"
             aria-modal="true" aria-label="${t("crop-avatar")}">
          <div class="crop-modal">
            <div class="crop-header">
              <span class="crop-title">${t("crop-photo")}</span>
              <button class="crop-close-btn" id="crop-close" aria-label="${t("cancel")}">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor"
                        stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>

            <div class="crop-stage" id="crop-stage">
              <img id="crop-img" alt="" draggable="false" />
              <div class="crop-guide"></div>
            </div>

            <div class="crop-controls">
              <span class="zoom-icon">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.3"/>
                  <path d="M11 11l3 3M7 5v4M5 7h4" stroke="currentColor"
                        stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </span>
              <input type="range" id="crop-zoom" min="0.5" max="5" step="0.01" value="1" />
              <span class="zoom-val" id="zoom-val">1.0×</span>
            </div>

            <p class="crop-hint">${t("crop-hint")}</p>

            <div class="crop-actions">
              <button class="btn btn-cancel" id="crop-cancel">${t("cancel")}</button>
              <button class="btn btn-apply"  id="crop-apply">${t("apply")}</button>
            </div>
          </div>
        </div>
      `;

      this._renderAvatar();
    }
  }

export { AvatarPicker }